/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { type FollowUpPriority, type FollowUpType } from '../../lib/api/follow-ups.js';
import { useCreateFollowUp } from '../../lib/queries/follow-ups.js';

const TYPES: readonly FollowUpType[] = [
  'custom',
  'payment_overdue',
  'intake_pending',
  'session_missed',
  'document_pending',
  'review_due',
];
const PRIORITIES: readonly FollowUpPriority[] = ['low', 'medium', 'high', 'urgent'];

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300';

/** "New follow-up" — admin raises a manual escalation task. */
export function CreateFollowUpModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateFollowUp();
  const [type, setType] = useState<FollowUpType>('custom');
  const [priority, setPriority] = useState<FollowUpPriority>('medium');
  const [title, setTitle] = useState('');
  const [slaDueAt, setSlaDueAt] = useState('');

  const onType = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setType(e.target.value as FollowUpType);
  }, []);
  const onPriority = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPriority(e.target.value as FollowUpPriority);
  }, []);
  const onTitle = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);
  const onSla = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSlaDueAt(e.target.value);
  }, []);
  const onModal = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );
  const submit = useCallback(() => {
    if (title.trim().length < 2) {
      return;
    }
    create.mutate(
      {
        type,
        priority,
        title: title.trim(),
        ...(slaDueAt !== '' ? { slaDueAt: new Date(slaDueAt).toISOString() } : {}),
      },
      {
        onSuccess: () => {
          setTitle('');
          setSlaDueAt('');
          onClose();
        },
      },
    );
  }, [create, type, priority, title, slaDueAt, onClose]);

  return (
    <Modal
      open={open}
      onOpenChange={onModal}
      title={t('followUps.newTitle')}
      closeLabel={t('common.cancel')}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="fu-type" className={LABEL}>
              {t('followUps.type')}
            </label>
            <select id="fu-type" className={INPUT} value={type} onChange={onType}>
              {TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`followUps.types.${tp}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="fu-prio" className={LABEL}>
              {t('followUps.priority')}
            </label>
            <select id="fu-prio" className={INPUT} value={priority} onChange={onPriority}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`followUps.priorities.${p}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="fu-title" className={LABEL}>
            {t('followUps.titleLabel')}
          </label>
          <input id="fu-title" className={INPUT} value={title} onChange={onTitle} />
        </div>
        <div>
          <label htmlFor="fu-sla" className={LABEL}>
            {t('followUps.sla')}
          </label>
          <input id="fu-sla" type="date" className={INPUT} value={slaDueAt} onChange={onSla} />
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={submit} disabled={create.isPending}>
            {t('followUps.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
